import { Signal } from '@angular/core';
import { Canvas, TPointerEventInfo } from 'fabric';
import { DrawingToolName } from 'shared/classroom/toolbar/core';
import {
    applyStylesToSelection,
    DashedPencilBrush,
    deleteObjects,
    type DrawStyle,
    ShapeDrawingController,
} from 'shared/fabric-drawing/tools';

/** Mouse handlers for the manual (ellipse/arrow) drawing tools. */
interface ShapeHandlers {
    down: (o: TPointerEventInfo) => void;
    move: (o: TPointerEventInfo) => void;
    up: (o: TPointerEventInfo) => void;
}

/** Reactive inputs the drawing-tools subsystem reads (real Angular `Signal`s, Decision C13). */
export interface SlideDrawingToolInputs {
    isWriter: Signal<boolean>;
    activeTool: Signal<DrawingToolName | null>;
    drawStyle: Signal<DrawStyle>;
}

/**
 * SlideDrawingTools — the freehand/shape drawing subsystem for a fabric slide canvas
 * (`canvas-renderer-composition`), kept out of `SlideCanvasRendererComponent` so the renderer stays a pure
 * compositor (recipe → capabilities → pointer/z-band dispatch → viewport) and this cohesive concern
 * lives — and is unit-tested — on its own.
 *
 * It owns everything about turning the toolbar's active tool + stroke style into canvas behaviour:
 * the tool-mode configuration (`applyTool`), the manual ellipse/arrow mouse handlers on the shared
 * `ShapeDrawingController`, the Delete/Backspace deletion of the selected drawing, and the live
 * brush/selection restyle. It couples ONLY to the `Canvas` and three reactive inputs
 * (`isWriter`/`activeTool`/`drawStyle`) — nothing about slide types, backgrounds, capabilities, or pan
 * — so it's `new`-able with plain `signal()`s in a test, no TestBed.
 *
 * Reactivity is owned by the host (Decision C13): the host's `effect`s call {@link applyTool} /
 * {@link refreshStyle}; this class holds no effects. The Delete/Backspace `keydown` listener is a
 * plain document listener set up in the constructor and removed in {@link dispose}.
 */
export class SlideDrawingTools {
    private readonly shapeController: ShapeDrawingController;
    private shapeHandlers?: ShapeHandlers;
    private readonly keydownListener: (e: KeyboardEvent) => void;

    constructor(
        private readonly canvas: Canvas,
        private readonly inputs: SlideDrawingToolInputs,
    ) {
        this.shapeController = new ShapeDrawingController(this.canvas);
        this.keydownListener = e => this.handleKeydown(e);
        document.addEventListener('keydown', this.keydownListener);
    }

    /**
     * Configure the canvas for the current active tool. Non-writers and the null/selection cases get no
     * drawing; `selection` enables object select/move (writers); `pencil` enables fabric free-drawing;
     * `ellipse`/`arrow` wire manual mouse handlers onto the shared controller.
     */
    applyTool(): void {
        const canvas = this.canvas;
        const tool = this.inputs.activeTool();
        this.detachShapeHandlers();
        const isWriter = this.inputs.isWriter();

        if (!isWriter || tool === null || tool === 'selection') {
            const selectable = isWriter && tool === 'selection';
            canvas.isDrawingMode = false;
            canvas.selection = selectable;
            canvas.skipTargetFind = !selectable;
            return;
        }

        if (tool === 'pencil') {
            canvas.selection = false;
            canvas.skipTargetFind = false;
            canvas.isDrawingMode = true;
            const style = this.inputs.drawStyle();
            canvas.freeDrawingBrush = DashedPencilBrush.createForStyle(
                canvas,
                style.brushStyle,
                style.color,
                style.width,
            );
            return;
        }

        // ellipse | arrow — handled via manual mouse events on the shared controller.
        canvas.isDrawingMode = false;
        canvas.selection = false;
        canvas.skipTargetFind = true;
        this.attachShapeHandlers(tool);
    }

    /**
     * React to color/width changes: refresh the pencil brush and recolor any current selection
     * (a no-op when nothing is selected). Mirrors PT's behavior.
     */
    refreshStyle(): void {
        const style = this.inputs.drawStyle();
        if (!this.inputs.isWriter()) return;
        if (this.canvas.isDrawingMode) {
            this.canvas.freeDrawingBrush = DashedPencilBrush.createForStyle(
                this.canvas,
                style.brushStyle,
                style.color,
                style.width,
            );
        }
        applyStylesToSelection(this.canvas, style); // default: fires object:modified → binding syncs
    }

    dispose(): void {
        document.removeEventListener('keydown', this.keydownListener);
        this.detachShapeHandlers();
    }

    /**
     * Delete the selected drawing(s) with Delete/Backspace (writers only). Skips when in free-draw
     * mode, editing text, or typing in a form field.
     */
    private handleKeydown(e: KeyboardEvent): void {
        if (e.key !== 'Delete' && e.key !== 'Backspace') return;
        if (this.canvas.isDrawingMode || !this.inputs.isWriter()) return;

        const targetEl = e.target as HTMLElement | null;
        if (
            targetEl &&
            (targetEl.tagName === 'INPUT' || targetEl.tagName === 'TEXTAREA' || targetEl.isContentEditable)
        ) {
            return;
        }

        const active = this.canvas.getActiveObject();
        if (!active || (active as { isEditing?: boolean }).isEditing) return;

        deleteObjects(this.canvas, active);
        e.preventDefault();
    }

    private attachShapeHandlers(tool: 'ellipse' | 'arrow'): void {
        const canvas = this.canvas;
        const controller = this.shapeController;

        let drawing = false;
        const down = (o: TPointerEventInfo) => {
            const p = canvas.getScenePoint(o.e);
            if (tool === 'ellipse') {
                controller.startShape('ellipse', p.x, p.y, this.inputs.drawStyle());
            } else {
                controller.startArrow(p.x, p.y, this.inputs.drawStyle());
            }
            drawing = true;
        };
        const move = (o: TPointerEventInfo) => {
            if (!drawing) return;
            const p = canvas.getScenePoint(o.e);
            if (tool === 'ellipse') {
                controller.updateShape(p.x, p.y, o.e.shiftKey);
            } else {
                controller.updateArrow(p.x, p.y);
            }
        };
        const up = (o: TPointerEventInfo) => {
            if (!drawing) return;
            const p = canvas.getScenePoint(o.e);
            if (tool === 'ellipse') {
                controller.finishShape();
            } else {
                controller.finishArrow(p.x, p.y);
            }
            drawing = false;
        };

        this.shapeHandlers = { down, move, up };
        canvas.on('mouse:down', down);
        canvas.on('mouse:move', move);
        canvas.on('mouse:up', up);
    }

    private detachShapeHandlers(): void {
        const canvas = this.canvas;
        const h = this.shapeHandlers;
        if (h) {
            canvas.off('mouse:down', h.down);
            canvas.off('mouse:move', h.move);
            canvas.off('mouse:up', h.up);
        }
        this.shapeController.cancelShape();
        this.shapeController.cancelArrow();
        this.shapeHandlers = undefined;
    }
}
